import { useState, useRef, useEffect, useCallback, ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Gift,
  Heart,
  Star,
  Zap,
  ShoppingBag,
  Users,
  BotIcon,
  X,
  Search,
  User,
  Sparkles,
  Send,
  Mic,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import { Card } from "components/UI/Card";
import Button from "shared/Button/Button";
import { Badge } from "components/UI/Badge";
import { Separator } from "components/UI/Separator";
import { Input } from "components/UI/Input";
import { motion, AnimatePresence, Variants } from "framer-motion";
import toast from "react-hot-toast";
import {
  sendMessage,
  addUserMessage,
  fetchChatHistory,
  deactivateSession,
  selectMessages,
  selectCurrentSessionId,
  selectChatLoading,
  selectChatJobStatus,
  selectChatQueuePosition,
  selectChatEstimatedWaitTime,
  selectChatJobError,
  selectChatStreamingEnabled,
  selectChatIsStreaming,
  selectChatProgressNode,
  selectChatProgressMessage,
  setStreamingEnabledPreference,
  setCurrentSession,
  chatWebSocketStatus,
  chatWebSocketResponse,
  chatWebSocketError,
  chatWebSocketStreamChunk,
  chatWebSocketStreamDone,
  chatWebSocketStreamError,
} from "../../store/slices/aiChatSlice";
import chatWebSocketService from "../../services/chatWebSocketService";
import { ChatMessage, MessageRole } from "../../Interfaces/interface.chat";
import { openModal } from "store/slices/authModalSlice";
import { useAppDispatch, useAppSelector } from "store/hooks/hooks";
import { selectUser } from "../../store/slices/userSlice";
import ConfirmDialog from "shared/Dialog/ConfirmDialog ";
import { initializeDeviceFingerprint } from "utils/deviceFingerprint";
import AudioRecorder from "./AudioRecorder";
import { getUserId } from "@/services/cookieService";

const quickSearchButtons = [
  {
    icon: Gift,
    label: "Corporate Gifts",
    query: "Show me corporate gift ideas for business clients",
    gradient: "from-blue-500 to-blue-600",
  },
  {
    icon: Heart,
    label: "Employee Appreciation",
    query: "What are good employee appreciation gift ideas?",
    gradient: "from-pink-500 to-pink-600",
  },
  {
    icon: Star,
    label: "Premium Gifts",
    query: "Show me premium and luxury gift options",
    gradient: "from-purple-500 to-purple-600",
  },
  {
    icon: Zap,
    label: "Tech Gifts",
    query: "What are popular tech gifts for professionals?",
    gradient: "from-yellow-500 to-orange-500",
  },
  {
    icon: ShoppingBag,
    label: "Budget Friendly",
    query: "Show me budget-friendly gift options under ₹50",
    gradient: "from-green-500 to-green-600",
  },
  {
    icon: Users,
    label: "Team Building",
    query: "What are good team building and group gift ideas?",
    gradient: "from-indigo-500 to-indigo-600",
  },
];

interface AIConversationProps {
  initialQuery?: string;
  userId: string | null;
  onClose: () => void;
  onProductsFound: (products: any[]) => void;
}

const AIConversation: React.FC<AIConversationProps> = ({
  initialQuery = "",
  userId,
  onClose,
  onProductsFound,
}) => {
  const [inputValue, setInputValue] = useState("");
  const navigate = useNavigate();
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [showQuickButtons, setShowQuickButtons] = useState(true);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const dispatch = useAppDispatch();
  const messages = useSelector(selectMessages);
  const currentSessionId = useSelector(selectCurrentSessionId);
  const isLoading = useSelector(selectChatLoading);
  const jobStatus = useSelector(selectChatJobStatus);
  const queuePosition = useSelector(selectChatQueuePosition);
  const estimatedWaitTime = useSelector(selectChatEstimatedWaitTime);
  const jobError = useSelector(selectChatJobError);
  const streamingEnabled = useSelector(selectChatStreamingEnabled);
  const isStreaming = useSelector(selectChatIsStreaming);
  const progressNode = useSelector(selectChatProgressNode);
  const progressMessage = useSelector(selectChatProgressMessage);
  const user = useSelector(selectUser);
  const userIdNew = useSelector(getUserId);
  const [isFirstMount, setIsFirstMount] = useState(true);
  const [hasPreviousSession, setHasPreviousSession] = useState(false);
  const MAX_MESSAGE_LENGTH = 1000;




  const [isAudioMode, setIsAudioMode] = useState(false);

  const jobStatusLabel = (() => {
    switch (jobStatus) {
      case "queued":
        return "Request accepted • waiting for worker";
      case "processing":
        return "AI is processing your request";
      default:
        return jobStatus ? `Job status: ${jobStatus}` : null;
    }
  })();


  const inputVariants: Variants = {
    initial: { opacity: 0, y: 20 },
    animate: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3, ease: "easeOut" as const },
    },
    exit: { opacity: 0, y: -20, transition: { duration: 0.1, ease: "easeIn" as const } },
  };

  // WebSocket connection management
  useEffect(() => {

    if (!userIdNew) return;

    // Initialize device fingerprint
    initializeDeviceFingerprint();

    // Connect WebSocket
    chatWebSocketService.connect(undefined, {
      onStatus: (event) => {
        dispatch(chatWebSocketStatus(event));
      },
      onResponse: (event) => {
        dispatch(chatWebSocketResponse(event));
      },
      onError: (event) => {
        dispatch(chatWebSocketError(event));
      },
      onStreamChunk: (event) => {
        dispatch(chatWebSocketStreamChunk(event));
      },
      onStreamDone: (event) => {
        dispatch(chatWebSocketStreamDone(event));
      },
      onStreamError: (event) => {
        dispatch(chatWebSocketStreamError(event));
      },
      onConnect: () => {
        console.info("Chat WebSocket connected");
        setIsSocketConnected(true);
        toast.dismiss("ws-reconnect");
      },
      onDisconnect: () => {
        console.warn("Chat WebSocket disconnected");
        setIsSocketConnected(false);
      },
      onReconnect: () => {
        console.info("Chat WebSocket reconnecting...");
        toast.loading("Reconnecting to chat service...", { id: "ws-reconnect" });
      },
    });

    // Load chat history if session exists
    const storedSessionId = sessionStorage.getItem(`chatSessionId_${userIdNew}`);
    if (storedSessionId) {
      setHasPreviousSession(true);
      dispatch(setCurrentSession({ sessionId: storedSessionId }));
      dispatch(
        fetchChatHistory({ userId: userIdNew!, sessionId: storedSessionId })
      );
    }

    setIsFirstMount(false);

    // Cleanup: disconnect WebSocket when component unmounts
    return () => {
      // chatWebSocketService.disconnect();
      // setIsSocketConnected(false);
      toast.dismiss("ws-reconnect");
    };
  }, [userIdNew, dispatch]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleInitialQuery = useCallback(() => {



    if (!userIdNew) {
      console.log("userIdNew", userIdNew);
      dispatch(openModal());
      return;
    }
    if (initialQuery.length > MAX_MESSAGE_LENGTH) {
      toast.error(`Message exceeds ${MAX_MESSAGE_LENGTH} character limit.`);
      return;
    }

    // Check WebSocket connection - use local state first to avoid race condition
    if (!isSocketConnected) {
      console.log("WebSocket not connected yet, waiting...");
      return;
    }

    if (!chatWebSocketService.isConnected()) {
      toast.error("Not connected to chat service. Please wait a moment and try again.");
      return;
    }

    dispatch(addUserMessage({ message: initialQuery }));
    dispatch(
      sendMessage({
        message: initialQuery,
        userId: userIdNew,
        sessionId: currentSessionId || undefined,
      })
    ).then(() => {
      setShowQuickButtons(false);
    });
    setShowQuickButtons(false);
  }, [userIdNew, initialQuery, dispatch, currentSessionId, isSocketConnected]);

  useEffect(() => {
    if (initialQuery && userIdNew && isSocketConnected) {
      handleInitialQuery();
    }
  }, [initialQuery, userIdNew, isSocketConnected, handleInitialQuery]);

  useEffect(() => {
    if (
      showQuickButtons &&
      messages.length === 0 &&
      scrollContainerRef.current
    ) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [showQuickButtons, messages.length]);

  useEffect(() => {
    if (!showQuickButtons && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, showQuickButtons]);

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    console.log("jai shree ram")
    // if (!inputValue.trim() || !userId) {
    //   if (!userId) dispatch(openModal());
    //   return;
    // }
    if (inputValue.length > MAX_MESSAGE_LENGTH) {
      toast.error(`Message exceeds ${MAX_MESSAGE_LENGTH} character limit.`);
      return;
    }

    // Check WebSocket connection
    console.error('Connection Check Failed - isConnected:', chatWebSocketService.isConnected(), 'readyState:', chatWebSocketService.getReadyState());
    if (!chatWebSocketService.isConnected()) {
      toast.error("Not connected to chat service. Please wait a moment and try again.");
      return;
    }

    dispatch(addUserMessage({ message: inputValue }));
    dispatch(
      sendMessage({
        message: inputValue,
        userId: userIdNew,
        sessionId: currentSessionId || undefined,
      })
    ).then(() => {
      setInputValue("");
      setShowQuickButtons(false);
    });
    setInputValue("");
    setShowQuickButtons(false);
  };

  const handleQuickButtonClick = (query: string) => {
    if (!userIdNew) {
      dispatch(openModal());
      return;
    }
    if (query.length > MAX_MESSAGE_LENGTH) {
      toast.error(`Message exceeds ${MAX_MESSAGE_LENGTH} character limit.`);
      return;
    }

    // Check WebSocket connection
    if (!chatWebSocketService.isConnected()) {
      toast.error("Not connected to chat service. Please wait a moment and try again.");
      return;
    }

    dispatch(addUserMessage({ message: query }));
    dispatch(
      sendMessage({
        message: query,
        userId: userIdNew,
        sessionId: currentSessionId || undefined,
      })
    ).then(() => {
      setShowQuickButtons(false);
    });
    setShowQuickButtons(false);
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInputValue(suggestion);
  };

  const handleShowResults = () => {
    const latestAIMessage = messages
      .filter((msg: ChatMessage) => msg.role === MessageRole.ASSISTANT)
      .slice(-1)[0];
    const products = latestAIMessage?.products || [
      { id: 1, name: "Premium Tech Bundle", price: 45, category: "Tech" },
      { id: 2, name: "Eco-Friendly Gift Set", price: 35, category: "Eco" },
      {
        id: 3,
        name: "Custom Branded Drinkware",
        price: 25,
        category: "Drinkware",
      },
    ];
    onProductsFound(products);
    onClose();
  };

  const handleCloseClick = () => {
    console.log('currentSessionId: ', currentSessionId);
    if (currentSessionId) {
      setShowConfirmDialog(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    if (userIdNew && currentSessionId) {
      dispatch(
        deactivateSession({
          userId: userIdNew,
          sessionId: currentSessionId,
          reason: "User closed session",
        })
      ).then(() => {
        sessionStorage.removeItem(`chatSessionId_${userIdNew}`);
        setShowConfirmDialog(false);
        onClose();
      });
    } else {
      setShowConfirmDialog(false);
      onClose();
    }
  };

  const handleKeepActive = () => {
    setShowConfirmDialog(false);
    onClose();
  };

  const containerVariants = {
    initial: {},
    animate: { transition: { staggerChildren: 0.1 } },
  };

  const childVariants: Variants = {
    initial: { opacity: 1, y: -20 },
    animate: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.1, ease: "easeOut" as const },
    },
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-4xl h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center">
              <BotIcon className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-sm md:text-lg flex items-center gap-2">
                ELVA{" "}
                <span className="flex items-center gap-1 text-white text-xs font-extralight py-0.5 px-2 rounded-xl bg-gradient-to-br from-blue-600/50 to-purple-600/50">
                  Beta
                </span>
              </h3>
              <p className="md:text-sm text-xs text-gray-600">
                Let's find your perfect gifts together
              </p>
            </div>
          </div>
          <Button
            onClick={() => {
              if (isAudioMode) return;
              handleCloseClick();
            }}
            disabled={isAudioMode}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div
          className="flex-1 overflow-y-auto p-6 space-y-4 overflow-x-hidden"
          ref={scrollContainerRef}
        >
          {showQuickButtons && messages.length === 0 && (
            <motion.div
              className="space-y-4"
              initial="initial"
              animate="animate"
              variants={containerVariants}
            >
              <div className="text-center">
                <h4 className="text-lg font-semibold text-gray-800 mb-2">
                  What can I help you find today?
                </h4>
                <p className="text-sm flex justify-center items-center text-gray-600 mb-6 font-bold">
                  Select one of the frequently asked questions or type your own
                  message
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {quickSearchButtons.map((button, index) => {
                  const IconComponent = button.icon;
                  return (
                    <motion.button
                      key={`${button.label}-${index}`}
                      custom={index}
                      variants={childVariants}
                      onClick={() => {
                        if (isAudioMode) return;
                        handleQuickButtonClick(button.query);
                      }}
                      disabled={isAudioMode}
                      className={`group relative p-3 sm:p-4 rounded-xl bg-gradient-to-r ${button.gradient
                        } ${isAudioMode ? "opacity-50 cursor-not-allowed" : ""
                        } text-white hover:shadow-lg hover:scale-[1.02] transition-all duration-200 flex items-center gap-3 w-full`}
                    >
                      <div className="w-9 h-9 sm:w-8 sm:h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <IconComponent className="w-4 h-4 sm:w-4 sm:h-4" />
                      </div>
                      <div className="text-left flex-1">
                        <p className="font-medium text-sm sm:text-base">
                          {button.label}
                        </p>
                        <p className="text-xs sm:text-sm text-white/80">
                          {button.query.slice(0, 30)}...
                        </p>
                      </div>
                      <Search className="w-4 h-4 ml-auto opacity-60 group-hover:opacity-100 transition-opacity hidden sm:inline" />
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          )}
          {messages.map((message: ChatMessage) => (
            <div
              key={message.id}
              className={`flex ${message.role === MessageRole.USER
                ? "justify-end"
                : "justify-start"
                }`}
            >
              <div
                className={`max-w-[80%] ${message.role === MessageRole.USER ? "order-2" : "order-1"
                  }`}
              >
                <div
                  className={`flex items-start space-x-3 ${message.role === MessageRole.USER
                    ? "flex-row-reverse space-x-reverse"
                    : ""
                    }`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${message.role === MessageRole.USER
                      ? "bg-blue-600"
                      : "bg-gradient-to-br from-purple-500 to-pink-500"
                      }`}
                  >
                    {message.role === MessageRole.USER ? (
                      <User className="h-4 w-4 text-white" />
                    ) : (
                      <Sparkles className="h-4 w-4 text-white" />
                    )}
                  </div>
                  <div
                    className={`rounded-2xl p-4 ${message.role === MessageRole.USER
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-gray-900"
                      }`}
                  >
                    {message.role === MessageRole.USER ? (
                      <p className="whitespace-pre-wrap">{message.content}</p>
                    ) : (
                      <div className="prose prose-sm max-w-none">
                        <ReactMarkdown
                          components={{
                            h1: ({ node, ...props }) => (
                              <h1
                                className="text-lg font-bold mb-2"
                                {...props}
                              />
                            ),
                            h2: ({ node, ...props }) => (
                              <h2
                                className="text-md font-semibold mb-2"
                                {...props}
                              />
                            ),
                            h3: ({ node, ...props }) => (
                              <h3
                                className="text-sm font-medium mb-1"
                                {...props}
                              />
                            ),
                            p: ({ node, ...props }) => (
                              <p className="mb-2 last:mb-0" {...props} />
                            ),
                            ul: ({ node, ...props }) => (
                              <ul
                                className="list-disc list-inside mb-2 space-y-1"
                                {...props}
                              />
                            ),
                            ol: ({ node, ...props }) => (
                              <ol
                                className="list-decimal list-inside mb-2 space-y-1"
                                {...props}
                              />
                            ),
                            li: ({ node, ...props }) => (
                              <li className="text-sm" {...props} />
                            ),
                            strong: ({ node, ...props }) => (
                              <strong className="font-semibold" {...props} />
                            ),
                            em: ({ node, ...props }) => (
                              <em className="italic" {...props} />
                            ),
                            code: ({ node, ...props }) => (
                              <code
                                className="bg-gray-200 px-1 py-0.5 rounded text-xs font-mono"
                                {...props}
                              />
                            ),
                            blockquote: ({ node, ...props }) => (
                              <blockquote
                                className="border-l-2 border-gray-300 pl-3 italic"
                                {...props}
                              />
                            ),
                          }}
                        >
                          {message.content}
                        </ReactMarkdown>
                      </div>
                    )}
                    {message.products && message.products.length > 0 && (
                      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {message.products.map((product: any, index: number) => (
                          <div
                            key={product.id || index}
                            className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow cursor-pointer group"
                            onClick={() => {
                              navigate(`/product/${product.id}`);
                              onClose();
                            }}
                          >
                            <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
                              {product.image ? (
                                <img
                                  src={product.image}
                                  alt={product.name}
                                  className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400">
                                  <ShoppingBag className="w-8 h-8 opacity-20" />
                                </div>
                              )}
                            </div>
                            <div className="p-3">
                              <h4 className="font-medium text-sm text-gray-900 line-clamp-1">
                                {product.name}
                              </h4>
                              {product.price && (
                                <p className="text-sm font-semibold text-indigo-600 mt-1">
                                  {product.price}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {message.suggestions && message.suggestions.length > 0 && (
                      <div className="mt-3 space-y-2">
                        <p className="text-sm font-medium">Quick suggestions:</p>
                        <div className="flex flex-wrap gap-2">
                          {message.suggestions.map((suggestion, index) => (
                            <Badge
                              key={index}
                              variant="outline"
                              className={`cursor-pointer hover:bg-white/20 ${isAudioMode
                                ? "opacity-50 pointer-events-none"
                                : ""
                                }`}
                              onClick={() => {
                                if (isAudioMode) return;
                                if (
                                  suggestion.toLowerCase().includes("view") ||
                                  suggestion.toLowerCase().includes("show") ||
                                  suggestion.toLowerCase().includes("results")
                                ) {
                                  handleShowResults();
                                } else {
                                  handleSuggestionClick(suggestion);
                                }
                              }}
                            >
                              {suggestion}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1 px-11">
                  {new Date(message.timestamp).toLocaleTimeString()}
                </p>
              </div>
            </div>
          ))}

          {progressMessage && (
            <div className="flex justify-center">
              <div className="bg-white/70 border border-indigo-100 rounded-2xl px-4 py-2 text-[11px] md:text-xs text-indigo-700 shadow-sm">
                {progressNode ? (
                  <span className="font-semibold mr-1">{progressNode}:</span>
                ) : null}
                <span>{progressMessage}</span>
              </div>
            </div>
          )}
          {jobError && !isLoading && (
            <div className="flex justify-center">
              <div className="bg-red-50 border border-red-100 rounded-2xl px-4 py-3 text-xs md:text-sm text-red-700 w-full md:w-auto">
                {jobError}
              </div>
            </div>
          )}
          {isLoading && (
            <div className="flex justify-start">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <Sparkles className="h-4 w-4 text-white animate-pulse" />
                </div>
                <div className="bg-gray-100 rounded-2xl p-4">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce delay-100"></div>
                    <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce delay-200"></div>
                  </div>
                  <p className="text-[13px] text-indigo-500 mt-2">
                    {jobStatusLabel}
                  </p>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        <Separator />
        <div className="p-6">
          <form
            onSubmit={handleSendMessage}
            className="flex space-x-3 min-h-[56px]"
          >
            <AnimatePresence mode="wait">
              {isAudioMode ? (
                <motion.div
                  key="audio"
                  variants={inputVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  className="flex-1"
                >
                  <AudioRecorder
                    onTranscribed={(text) => {
                      setInputValue(text);
                      setIsAudioMode(false);
                      if (text.trim() && userIdNew) {
                      }
                    }}
                    onCancel={() => setIsAudioMode(false)}
                    userId={userIdNew}
                    sessionId={currentSessionId}
                  />
                </motion.div>
              ) : (
                <motion.div
                  key="text"
                  variants={inputVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  className="flex flex-1 space-x-3 justify-center items-center"
                >
                  <Input
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder="Tell Me Your Requirements..."
                    className="flex-1 text-xs md:text-sm"
                    disabled={isLoading}
                    maxLength={MAX_MESSAGE_LENGTH}
                  />
                  <Button
                    type="button"
                    onClick={() => setIsAudioMode(true)}
                    disabled={isLoading}
                    className="px-3"
                  >
                    <Mic className="h-4 w-4" />
                  </Button>
                  <Button
                    type="submit"
                    disabled={isLoading || !inputValue.trim()}
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </form>
        </div>
      </Card>
      <ConfirmDialog
        isOpen={showConfirmDialog}
        onClose={handleKeepActive}
        onConfirm={handleConfirmClose}
        title="Close Chat Session"
        message="Do you want to close and deactivate this chat session? You can keep it active to continue later."
        loading={isLoading}
        cancelText="Keep Active"
        confirmText="Close Session"
      />
    </div>
  );
};

export default AIConversation;
